import { useState, useEffect, useCallback } from 'react';
import { getAuditLogs } from '../lib/api';
import { useApi } from '../hooks/useApi';
import type { AuditLog as AuditLogType } from '../types';

const actionTypeLabels: Record<string, string> = {
  suspend: 'Suspend Account',
  close: 'Close Account',
  add_note: 'Add Note',
  view_profile: 'View Profile',
  interaction: 'Interaction',
  api_call: 'API Call',
};

const actionTypeColors: Record<string, string> = {
  suspend: 'badge-warning',
  close: 'badge-danger',
  add_note: 'badge-info',
  view_profile: 'badge-gray',
  interaction: 'badge-info',
  api_call: 'badge-gray',
};

export default function AuditLog() {
  const [logs, setLogs] = useState<AuditLogType[]>([]);
  const [filters, setFilters] = useState({
    customerId: '',
    agentEmail: '',
  });
  const [pagination, setPagination] = useState({
    limit: 50,
    offset: 0,
  });

  const { loading, error, execute } = useApi(getAuditLogs);

  const fetchLogs = useCallback(async () => {
    const result = await execute({
      customerId: filters.customerId || undefined,
      agentEmail: filters.agentEmail || undefined,
      limit: pagination.limit,
      offset: pagination.offset,
    });
    if (result) {
      setLogs(result.logs);
    }
  }, [execute, filters, pagination]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPagination((prev) => ({ ...prev, offset: 0 }));
    fetchLogs();
  };

  const handleClear = () => {
    setFilters({ customerId: '', agentEmail: '' });
    setPagination({ limit: 50, offset: 0 });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Audit Log</h1>
        <p className="mt-1 text-sm text-gray-500">
          View all CX agent actions and activity.
        </p>
      </div>

      <form onSubmit={handleSearch} className="card">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label htmlFor="customerId" className="label">
              Customer ID
            </label>
            <input
              type="text"
              id="customerId"
              className="input"
              placeholder="Filter by customer ID..."
              value={filters.customerId}
              onChange={(e) => setFilters({ ...filters, customerId: e.target.value })}
            />
          </div>

          <div>
            <label htmlFor="agentEmail" className="label">
              Agent Email
            </label>
            <input
              type="text"
              id="agentEmail"
              className="input"
              placeholder="Filter by agent email..."
              value={filters.agentEmail}
              onChange={(e) => setFilters({ ...filters, agentEmail: e.target.value })}
            />
          </div>

          <div className="flex items-end space-x-3">
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Loading...' : 'Filter'}
            </button>
            <button type="button" onClick={handleClear} className="btn-secondary">
              Clear
            </button>
          </div>
        </div>
      </form>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Timestamp
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Agent
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Action
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {logs.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center text-sm text-gray-500">
                    {loading ? 'Loading...' : 'No audit logs found.'}
                  </td>
                </tr>
              ) : (
                logs.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(log.created_at).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {log.agent_name || log.agent_email.split('@')[0]}
                      </div>
                      <div className="text-xs text-gray-500">{log.agent_email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={actionTypeColors[log.action_type] || 'badge-gray'}>
                        {actionTypeLabels[log.action_type] || log.action_type}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-mono text-gray-900">{log.customer_id}</div>
                      {log.customer_email && (
                        <div className="text-xs text-gray-500">{log.customer_email}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {log.response_status ? (
                        log.response_status >= 200 && log.response_status < 300 ? (
                          <span className="badge-success">{log.response_status}</span>
                        ) : (
                          <span className="badge-danger">{log.response_status}</span>
                        )
                      ) : (
                        <span className="badge-gray">N/A</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {logs.length > 0 && (
          <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-500">
              Showing {pagination.offset + 1} - {pagination.offset + logs.length}
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() =>
                  setPagination((prev) => ({
                    ...prev,
                    offset: Math.max(0, prev.offset - prev.limit),
                  }))
                }
                disabled={pagination.offset === 0}
                className="btn-secondary text-sm"
              >
                Previous
              </button>
              <button
                onClick={() =>
                  setPagination((prev) => ({
                    ...prev,
                    offset: prev.offset + prev.limit,
                  }))
                }
                disabled={logs.length < pagination.limit}
                className="btn-secondary text-sm"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
